import graphql from "babel-plugin-relay/macro";
import { useEffect } from "react";
import { readInlineData } from "react-relay";

import { disableSentry, initializeSentry } from "../sentry/initializeSentry";
import { initializeMixpanel } from "./Mixpanel/useInitializeMixpanel/useInitializeMixpanel";
import {
  useSyncAnalyticsTrackingPermission_session$data,
  useSyncAnalyticsTrackingPermission_session$key,
} from "./__generated__/useSyncAnalyticsTrackingPermission_session.graphql";
import {
  setAnalyticsTrackingAllowed,
  shouldAllowAnalyticsTracking,
} from "./analyticsTrackingConsent";

export const readAnalyticsTrackingPermissionSession = (
  sessionRef: useSyncAnalyticsTrackingPermission_session$key | null | undefined
): useSyncAnalyticsTrackingPermission_session$data | null => {
  return (
    readInlineData(
      useSyncAnalyticsTrackingPermissionFragmentString,
      sessionRef ?? null
    ) ?? null
  );
};

export const useSyncAnalyticsTrackingPermission = (
  sessionRef: useSyncAnalyticsTrackingPermission_session$key | null | undefined
): boolean => {
  const session = readAnalyticsTrackingPermissionSession(sessionRef);
  const shouldTrack = shouldAllowAnalyticsTracking({
    hasAuthenticatedUser: !!session?.user,
    allowUnauthenticatedAnalyticsTracking:
      session?.allowUnauthenticatedAnalyticsTracking,
    requiresAnalyticsTrackingConsent: session?.requiresAnalyticsTrackingConsent,
    analyticsTrackingConsentedAt:
      session?.user?.__typename === "MemberUser"
        ? session.user.metadata?.analyticsTrackingConsentedAt
        : null,
    clubDefaultCountryCode:
      session?.currentClientProfile?.member?.club?.defaultCountryCode,
  });

  useEffect(() => {
    setAnalyticsTrackingAllowed(shouldTrack);

    if (shouldTrack) {
      initializeSentry();
      initializeMixpanel();
    } else {
      disableSentry();
    }
  }, [shouldTrack]);

  return shouldTrack;
};

export const useSyncAnalyticsTrackingPermissionFragmentString = graphql`
  fragment useSyncAnalyticsTrackingPermission_session on Session @inline {
    allowUnauthenticatedAnalyticsTracking
    requiresAnalyticsTrackingConsent
    user {
      __typename
      ... on MemberUser {
        metadata {
          analyticsTrackingConsentedAt
        }
      }
    }
    currentClientProfile {
      member {
        club {
          defaultCountryCode
        }
      }
    }
  }
`;
